import { ButtonType, ButtonWidget, CompactInlineAlertProps, CompactInlineAlertWidget, InlineAlertType, InteractionState } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import { SvgCertifiedIconActive } from '../../../gen-src/icons/CertifiedIconActive';
import { SvgCertifiedIconDefault } from '../../../gen-src/icons/CertifiedIconDefault';
import { DataQualityStatusIconButtonStyle } from '../style/DataQualityStyles';
import { DataQualityWarningKind, DataStatusIcon } from './DataQualityIndicatorSharedHelpers';

type DataQualityTooltipButtonBaseProps = {
  readonly testId: string;
};

function DataQualityCertificationButtonContent(props: { state?: InteractionState }): JSX.Element {
  const { state } = props;

  return (
    <div style={{
      backgroundColor: state?.active || state?.hover || state?.selected || false ? '#E5EDE4' : '#EDF5EC',
      color: '#666666',
      borderRadius: '12.5px',
      height: '25px',
      width: '100px',
    }}>
      <span style={{ position: 'relative', top: '5px', fontWeight: 'bold', fontSize: '12px' }}>
        <SvgCertifiedIconDefault />
      </span>
      <span style={{ position: 'relative', top: '3px', marginLeft: '4px', fontWeight: 'bold', fontSize: '12px' }}>
        {Messages.DataQualityTypeCertified()}
      </span>
    </div>
  );
}

function DataQualityCertificationIconContent(props: { state?: InteractionState }): JSX.Element {
  const { state } = props;

  return (
    <span>
      {
        state?.active || state?.hover || state?.selected || false ?
          <SvgCertifiedIconActive /> :
          <SvgCertifiedIconDefault />
      }
    </span>
  )
}

type DataQualityCertificationButtonprops = DataQualityTooltipButtonBaseProps & {
  readonly iconOnly: boolean;
  readonly onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  readonly tabIndex?: number;
}

export const DataQualityCertificationInteractiveButtonOrIcon = React.forwardRef<HTMLButtonElement, DataQualityCertificationButtonprops>(function DataQualityCertificationInteractiveButton(props, ref) {
  const style: React.CSSProperties = props.iconOnly ? {
    height: '14px',
    width: '14px'
  } : {
    borderRadius: '12.5px',
    height: '25px',
    width: '100px',
  }

  return (
    <ButtonWidget
      testId={props.testId}
      tabIndex={props.tabIndex}
      onClick={props.onClick}
      buttonRef={ref}
      style={style}
      title={''}
      buttonType={ButtonType.IconOutline}
      getContent={state => props.iconOnly ? <DataQualityCertificationIconContent state={state} /> : <DataQualityCertificationButtonContent state={state} />}
    />
  )
});

export function DataQualityCertificationNonInteractiveIcon(): JSX.Element {
  return (
    <SvgCertifiedIconDefault />
  );
}

type DataQualityWarningButtonBaseProps = {
  readonly warningKind: DataQualityWarningKind;
  readonly tabIndex?: number;
  readonly title: string;
}

type DataQualityWarningButtonProps = DataQualityTooltipButtonBaseProps & DataQualityWarningButtonBaseProps & {
  readonly onClick: CompactInlineAlertProps['onClick'];
}

type DataQualityWarningIconProps = DataQualityTooltipButtonBaseProps & DataQualityWarningButtonBaseProps & {
  readonly onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export const DataQualityWarningInteractiveButton = React.forwardRef<HTMLDivElement, DataQualityWarningButtonProps>(function DataQualityWarningInteractiveButton(props, ref) {
  const type = props.warningKind === 'severe-warning' ? InlineAlertType.Warning : InlineAlertType.Info;

  return (
    <CompactInlineAlertWidget testId={props.testId} type={type} onClick={props.onClick} ref={ref} tabIndex={props.tabIndex}>
      {props.title}
    </CompactInlineAlertWidget>
  );
});

export function DataQualityWarningNonInteractiveButton(props: Omit<DataQualityWarningButtonProps, 'onClick' | 'tabIndex'>): JSX.Element {
  const type = props.warningKind === 'severe-warning' ? InlineAlertType.Warning : InlineAlertType.Info;

  return (
    <CompactInlineAlertWidget testId={props.testId} type={type}>
      {props.title}
    </CompactInlineAlertWidget>
  );
}

export const DataQualityWarningInteractiveIcon = React.forwardRef<HTMLButtonElement, DataQualityWarningIconProps>(function DataQualityWarningInteractiveIcon(props, ref) {
  const isSevere = props.warningKind === 'severe-warning';

  return (
    <ButtonWidget
      testId={props.testId}
      buttonRef={ref}
      tabIndex={props.tabIndex}
      onClick={props.onClick}
      title={props.title}
      buttonType={ButtonType.IconOutline}
      getContent={s => <DataStatusIcon isSevere={isSevere} hover={s.hover || false} />}
      style={DataQualityStatusIconButtonStyle}
    />
  );
});
